import * as api from './api';
import { supabase } from '../supabaseClient';
import { getOrgId } from './orgStore';
import { fetchAllPayments } from './paymentService';

const fetchItemsByIds = async (itemIds, orgId) => {
  if (itemIds.length === 0) return {};
  if (!orgId) throw new Error('No org selected (org_id is null).');
  const { data, error } = await supabase
    .from('item')
    .select('item_id, quantity_on_hand, cost_excl, is_service_item')
    .in('item_id', itemIds)
    .eq('org_id', orgId);
  if (error) throw error;
  return Object.fromEntries((data || []).map((i) => [i.item_id, i]));
};

// --- Commented out: DB triggers on item_movement_log now handle stock adjustments ---
// const adjustItemQuantities = async (lineItems, direction) => {
//   const linesWithItems = lineItems.filter((l) => l.item_id);
//   if (linesWithItems.length === 0) return;
//
//   const itemIds = [...new Set(linesWithItems.map((l) => l.item_id))];
//   const itemMap = await fetchItemsByIds(itemIds);
//
//   const qtyChanges = {};
//   for (const line of linesWithItems) {
//     const item = itemMap[line.item_id];
//     if (!item || item.is_service_item) continue;
//     qtyChanges[line.item_id] = (qtyChanges[line.item_id] || 0) + parseFloat(line.qty || 0);
//   }
//
//   await Promise.all(
//     Object.entries(qtyChanges).map(([itemId, totalQty]) => {
//       const current = parseFloat(itemMap[itemId].quantity_on_hand || 0);
//       return supabase
//         .from('item')
//         .update({ quantity_on_hand: current + totalQty * direction })
//         .eq('item_id', itemId);
//     })
//   );
// };

/**
 * Fetch invoices plus all the dependency data the Invoice modal
 * and preview need (customers, items, taxes, setup, setup2).
 *
 * Also fetches all payments and enriches each invoice row with
 * `total_paid`, `amount_due`, and a `payments` array.
 */
export const fetchInvoices = async (searchTerm = '') => {
  const orgId = getOrgId();
  if (!orgId) throw new Error('No org selected (org_id is null).');

  const [invRes, custRes, itemRes, taxRes, setupRes, setup2Res, paymentsMap] =
    await Promise.all([
      supabase
        .from('invoices')
        .select('*, customers:customer_id(full_name)')
        .eq('org_id', orgId)
        .order('invoice_date', { ascending: false }),
      supabase.from('customers').select('*').eq('org_id', orgId).order('full_name', { ascending: true }),
      supabase.from('item_with_stock').select('*').eq('org_id', orgId).order('item_id', { ascending: true }),
      supabase.from('tax').select('*').or(`org_id.eq.${orgId},is_global.eq.true`),
      supabase.from('setup').select('*').eq('org_id', orgId),
      supabase.from('setup2').select('*').eq('org_id', orgId),
      fetchAllPayments(orgId),
    ]);

  if (invRes.error) throw invRes.error;
  if (custRes.error) throw custRes.error;
  if (itemRes.error) throw itemRes.error;
  if (taxRes.error) throw taxRes.error;
  if (setupRes.error) throw setupRes.error;
  if (setup2Res.error) throw setup2Res.error;

  let invoices = (invRes.data || []).map((inv) => {
    const payments = paymentsMap[inv.invoice_id] || [];
    const totalPaid = payments.reduce(
      (sum, p) => sum + parseFloat(p.payment_amount || 0),
      0
    );
    return {
      ...inv,
      payments,
      total_paid: totalPaid,
      amount_due: Math.max(parseFloat(inv.total_amount || 0) - totalPaid, 0),
    };
  });

  // Client-side search on invoice number or customer name
  if (searchTerm) {
    const term = searchTerm.toLowerCase();
    invoices = invoices.filter(
      (inv) =>
        inv.invoice_number?.toLowerCase().includes(term) ||
        inv.customers?.full_name?.toLowerCase().includes(term)
    );
  }

  return {
    invoices,
    customers: custRes.data || [],
    items: itemRes.data || [],
    taxes: taxRes.data || [],
    setup: setupRes.data || [],
    setup2: setup2Res.data || [],
  };
};

/**
 * Fetch line items for a specific invoice (used when editing).
 */
export const fetchInvoiceLines = async (invoiceId) => {
  const { data, error } = await supabase
    .from('invoice_line_items')
    .select('*')
    .eq('invoice_id', invoiceId);
  if (error) throw error;
  return data || [];
};

/**
 * Save an invoice (create or update).
 *
 * @param {{ header: object, lines: object[] }} payload
 * @param {number|null} existingInvoiceId – null for create
 */
export const saveInvoice = async ({ header, lines }, existingInvoiceId = null) => {
  const orgId = getOrgId();
  if (!orgId) throw new Error('No org selected (org_id is null).');

  // Map frontend field names to database column names
  const { billing_address, email, ...dbHeader } = header;
  if (billing_address) dbHeader.bill_to = billing_address;
  if (email !== undefined) dbHeader.customer_email = email;

  // Append current time to date-only fields so timestamps aren't midnight
  const now = new Date();
  const timeStr = now.toTimeString().split(' ')[0]; // HH:MM:SS
  if (dbHeader.invoice_date && dbHeader.invoice_date.length === 10) {
    dbHeader.invoice_date = `${dbHeader.invoice_date}T${timeStr}`;
  }
  if (dbHeader.due_date && dbHeader.due_date.length === 10) {
    dbHeader.due_date = `${dbHeader.due_date}T${timeStr}`;
  }

  let invoiceId = existingInvoiceId;

  // --- Stock reversal now handled by DB triggers on item_movement_log ---
  // if (existingInvoiceId) {
  //   const oldLines = await fetchInvoiceLines(existingInvoiceId);
  //   await adjustItemQuantities(oldLines, +1);
  // }

  if (existingInvoiceId) {
    // --- UPDATE ---
    const { error } = await supabase
      .from('invoices')
      .update(dbHeader)
      .eq('invoice_id', existingInvoiceId);
    if (error) throw error;
  } else {
    // --- CREATE ---
    // Check for duplicate invoice number
    if (dbHeader.invoice_number) {
      const { data: existing } = await supabase
        .from('invoices')
        .select('invoice_id')
        .eq('invoice_number', dbHeader.invoice_number)
        .eq('org_id', orgId)
        .maybeSingle();
      if (existing) {
        throw new Error(`Invoice #${dbHeader.invoice_number} already exists.`);
      }
    }

    // Let DB auto-generate invoice_id.
    // When invoice_number is left blank, a BEFORE INSERT trigger
    // (set_invoice_number) assigns the next per-org number, so each org
    // starts its invoice numbering at 1 instead of reusing the global id.
    const { invoice_id: _strip, ...insertPayload } = dbHeader;
    const { data, error } = await supabase
      .from('invoices')
      .insert([{ ...insertPayload, org_id: orgId }])
      .select()
      .single();
    if (error) throw error;
    invoiceId = data.invoice_id;
  }

  // --- SAVE LINE ITEMS (full-replace strategy) ---
  await supabase.from('invoice_line_items').delete().eq('invoice_id', invoiceId);

  // Look up current cost_excl from item table for each line
  const lineItemIds = [...new Set(lines.filter((l) => l.item_id).map((l) => l.item_id))];
  const itemMap = await fetchItemsByIds(lineItemIds, orgId);

  const linesToInsert = lines
    .map((line) => ({
      invoice_id: invoiceId,
      org_id: orgId,
      item_id: line.item_id || null,
      item_description: line.item_description,
      qty: line.quantity,
      unit_price: line.unit_price,
      line_discount_rate: line.line_discount_rate || 0,
      line_total_inclusive_tax: line.total_price,
      line_tax_amount: line.line_tax_amount || 0,
      tax_id: line.tax_rate_id || null,
      tax_rate: line.tax_rate != null ? line.tax_rate : null,
      cost_excl: line.item_id && itemMap[line.item_id]
        ? itemMap[line.item_id].cost_excl || 0
        : null,
    }))
    .filter((l) => l.item_description || l.unit_price > 0);

  if (linesToInsert.length > 0) {
    const { error: insertError } = await supabase
      .from('invoice_line_items')
      .insert(linesToInsert);
    if (insertError) throw insertError;
  }

  // --- Stock decrease now handled by DB triggers on item_movement_log ---
  // await adjustItemQuantities(linesToInsert, -1);

  return invoiceId;
};

/**
 * Delete an invoice and its line items.
 */
export const deleteInvoice = async (invoiceId) => {
  // --- Stock reversal now handled by DB triggers on item_movement_log ---
  // const oldLines = await fetchInvoiceLines(invoiceId);
  // await adjustItemQuantities(oldLines, +1);

  await supabase.from('invoice_line_items').delete().eq('invoice_id', invoiceId);
  await api.deleteRecord('invoices', 'invoice_id', invoiceId);
};
